import { Box, Card, CardHeader, Grid, Skeleton } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SnackbarCustom from '../../../../components/MUIComponents/SnackbarCustom';
import InfiniteScroll from '../../../../components/shared/InfiniteScroll/InfiniteScroll';
import { SnackbarSeverityEnum } from '../../../../enums/snackbar-severity-enum';
import { SORT_OPTIONS } from '../../../../enums/sort-options-enum';
import { IPostResponse } from '../../../../interfaces/post.interface';
import { useGetAllUsersPostsQuery } from '../../../../store/apiSlice/post/postApi';
import { useThemeContext } from '../../../../theme/ThemeContextProvider';
import NewPublicationTrigger from '../../shared-components/posts/NewPublicationTrigger';
import PostsToolbar from '../../shared-components/posts/PostsToolbar';
import ProfilePost from '../../shared-components/posts/ProfilePost';
import { Filter } from '../../shared-components/tabs/PostsTab';

// RENAMED: Was MyFriendsPosts, but actually shows ALL users' posts (public feed)
// This is the main LinkedIn-style feed showing posts from everyone on the platform
const PublicFeed = () => {
  const { theme } = useThemeContext();
  const [searchParams] = useSearchParams();

  const pageParam = parseInt(searchParams.get('page') || '1', 10);
  const limit = parseInt(searchParams.get('limit') || '10', 10);
  const search = searchParams.get('query') || '';

  const [sort, setSort] = useState<SORT_OPTIONS>(SORT_OPTIONS.DESC);
  const [filter, setFilter] = useState<Filter | null>(null);

  const [page, setPage] = useState<number>(pageParam);
  const [posts, setPosts] = useState<IPostResponse['data']>([]);
  const [hasMore, setHasMore] = useState<boolean>(false);

  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] =
    useState<SnackbarSeverityEnum>(SnackbarSeverityEnum.ERROR);

  const triggerSnackbar = (
    severity: SnackbarSeverityEnum,
    message: string
  ): void => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const queryParams = {
    tags: '',
    page,
    limit,
    search,
    sort,
    ...filter,
  };

  const {
    data: postsData,
    isLoading,
    isFetching,
    refetch: refetchPosts,
  } = useGetAllUsersPostsQuery(queryParams);

  // Reset page and clear posts when sort or filter changes
  useEffect(() => {
    setPage(1);
    setPosts([]);
  }, [sort, filter]);

  useEffect(() => {
    if (postsData) {
      const newPosts = postsData.data || [];
      const total = postsData.total || 0;

      const count = total - posts.length;
      const newItemsToAdd = newPosts.slice(-count);

      if (count > 0) {
        setPosts((prevPosts) =>
          page === 1 ? newPosts : [...prevPosts, ...newItemsToAdd]
        );
        const hasNext = total !== 0 && page * limit < total;
        setHasMore(hasNext);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postsData]);

  const fetchNextPage = () => {
    if (hasMore) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  const postsAsElements = useMemo(
    () =>
      posts.map((post) => (
        <Box
          key={post.id}
          className="profile-posts"
          display="flex"
          alignItems="center"
          sx={{
            backgroundColor: theme.palette.background.white,
          }}
        >
          <ProfilePost
            post={post}
            refetchPosts={refetchPosts}
            onSnackbar={triggerSnackbar}
          />
        </Box>
      )),
    [posts, refetchPosts, theme]
  );

  return (
    <>
      <Grid container direction="row">
        <Grid
          item
          flex={2}
          minWidth={0}
          rowGap={{ xs: '16px', xl: '22px' }}
          display="flex"
          flexDirection="column"
        >
          <NewPublicationTrigger refetchPosts={refetchPosts} />

          {isLoading ? (
            skeletonPostList
          ) : (
            <>
              <PostsToolbar
                sort={sort}
                setSort={setSort}
                postsCount={postsData?.total ?? null}
                setFilter={({ from, to }) => setFilter({ from, to })}
              />
              <InfiniteScroll
                allRows={postsAsElements}
                totalRows={postsData?.total ?? 0}
                fetchNextPage={fetchNextPage}
                isFetchingNextPage={isFetching}
              />
            </>
          )}
        </Grid>
      </Grid>

      <SnackbarCustom
        open={snackbarOpen}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </>
  );
};

export default PublicFeed;

const skeletonPost = (
  <Card
    sx={{
      width: '100%',
      padding: '0.5rem 1.3rem',
      backgroundColor: '#fff',
    }}
  >
    <CardHeader
      avatar={
        <Skeleton
          animation="wave"
          variant="circular"
          sx={{
            width: '2.5rem',
            height: '2.5rem',
          }}
        />
      }
      action={null}
      title={
        <Skeleton
          animation="wave"
          sx={{
            width: '80%',
            height: '1.125rem',
            marginBottom: '0.375rem',
          }}
        />
      }
      subheader={
        <Skeleton
          animation="wave"
          sx={{
            width: '40%',
            height: '0.75rem',
          }}
        />
      }
      sx={{ padding: '1rem 0' }}
    />
    <Skeleton
      animation="wave"
      variant="rectangular"
      sx={{ height: '21.875rem' }}
    />
    <Skeleton
      animation="wave"
      sx={{
        height: '2.5rem',
        margin: '0.625rem 0',
      }}
    />
  </Card>
);

const skeletonPostList = (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      padding: {
        xs: '0 0.5rem',
        lg: '0',
      },
    }}
  >
    <Box
      sx={{
        width: '100%',
        padding: '0.5rem 1.3rem',
        backgroundColor: '#fff',
      }}
    >
      <Skeleton
        sx={{
          width: '100%',
          minHeight: '2.5rem',
        }}
      />
    </Box>

    {[...Array(2)].map((_, index) =>
      React.cloneElement(skeletonPost, { key: index })
    )}
  </Box>
);
